// Data sync layer between the in-memory `data` shape and Supabase tables.
//
// The app stores data as a single nested object (the same shape as `defaultData`
// in src/utils/storage.js). Each section is mapped to one or more Supabase
// tables. This module is responsible for two things:
//
//   1. loadAll(userId)     → reconstruct the nested object from DB.
//   2. syncData(prev, next) → diff old vs new state and apply minimal
//      INSERT / UPDATE / DELETE operations to the relevant tables.
//
// All tables have RLS so we never need to filter by user_id manually for
// reads — the session does it for us.

import { supabase } from './supabase';
import { defaultData } from '../utils/storage';

// ---- Section ↔ table mapping -----------------------------------------------
// Collections: { sectionPath, table, [extraColumns(item)], [columns], [mapRow] }
//   columns : extra SELECT columns beyond 'id,data'
//   mapRow  : custom row → item mapper (defaults to { ...data, id })
const COLLECTIONS = [
  // NOTE: calendar_events is handled by syncCalendarEvents() below because
  // shared events must keep their *original* creator as user_id when written
  // back (otherwise upserts insert a duplicate row under the editing user's
  // id instead of updating the existing row).
  //
  // calendar birthdays and categories are similarly handled by custom
  // syncers below because they split across two tables (personal vs shared).
  // They are loaded separately in loadAll(); sync is explicit.

  { path: ['budget', 'income'], table: 'budget_income', extra: (it) => ({ date: it.date || null }) },
  { path: ['budget', 'expenses'], table: 'budget_expenses', extra: (it) => ({ date: it.date || null }) },
  { path: ['budget', 'savings'], table: 'budget_savings', extra: (it) => ({ date: it.date || null }) },
  { path: ['budget', 'expenseCategories'], table: 'budget_expense_categories' },
  { path: ['budget', 'incomeCategories'], table: 'budget_income_categories' },
  { path: ['budget', 'savingsCategories'], table: 'budget_savings_categories' },
  { path: ['budget', 'savingsAccounts'], table: 'budget_savings_accounts' },

  { path: ['fitness', 'sports'], table: 'fitness_sports' },
  { path: ['fitness', 'personalBests'], table: 'fitness_personal_bests' },

  // shopping_recipes / shopping_items are handled below by syncShoppingRecipes
  // and syncShoppingItems because rows can belong to a shared list (any member
  // can edit) and we must keep the original creator as user_id when writing
  // back to satisfy RLS.

  { path: ['todo', 'groups'], table: 'todo_groups' },
  // todo_tasks is handled by loadTodoTasks/syncTodoTasks below: tasks can be
  // shared into a calendar (members read them via RLS), so loading splits
  // own rows from other members' rows and syncing must only touch own rows.

  {
    path: ['diary', 'entries'],
    table: 'diary_entries',
    extra: (it) => ({ date: it.date || null })
  },

  { path: ['timetracker', 'companies'], table: 'timetracker_companies' },
  {
    path: ['timetracker', 'entries'],
    table: 'timetracker_entries',
    extra: (it) => ({
      started_at: it.startedAt || null,
      ended_at: it.endedAt || null
    })
  }
];

// ---- helpers ----------------------------------------------------------------
const getPath = (obj, path) => path.reduce((o, k) => (o == null ? o : o[k]), obj);

const setPath = (obj, path, value) => {
  const out = { ...obj };
  let cursor = out;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i];
    cursor[k] = { ...(cursor[k] || {}) };
    cursor = cursor[k];
  }
  cursor[path[path.length - 1]] = value;
  return out;
};

const sameRef = (a, b) => a === b;
const equalJSON = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Diff two arrays of items keyed by `id`.
const diffArray = (oldArr = [], newArr = []) => {
  const oldById = new Map((oldArr || []).map((it) => [String(it.id), it]));
  const newById = new Map((newArr || []).map((it) => [String(it.id), it]));
  const upserts = [];
  const deletes = [];
  for (const [id, it] of newById) {
    const prev = oldById.get(id);
    if (!prev || !equalJSON(prev, it)) upserts.push(it);
  }
  for (const [id] of oldById) {
    if (!newById.has(id)) deletes.push(id);
  }
  return { upserts, deletes };
};

const ensureId = (it) => {
  if (it && it.id != null) return it;
  return { ...it, id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}` };
};

// ---- Loading ----------------------------------------------------------------
async function loadCollection(table, columns = 'id,data', mapRow) {
  const { data, error } = await supabase.from(table).select(columns);
  if (error) throw error;
  const mapper = mapRow || ((row) => ({ ...(row.data || {}), id: row.id }));
  return (data || []).map(mapper);
}

async function loadSharedCalendars() {
  const { data, error } = await supabase
    .from('shared_calendars')
    .select('id,name,owner_id,created_at,has_birthdays,birthday_linked,birthday_category_id,digest_time,reminder_minutes,tz,holidays_enabled,holiday_category_id')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    name: r.name,
    ownerId: r.owner_id,
    createdAt: r.created_at,
    hasBirthdays: !!r.has_birthdays,
    birthdayLinked: r.birthday_linked !== false,
    birthdayCategoryId: r.birthday_category_id || 'purple',
    // Reminders + daily digest for every member (supabase/scheduled_notifications.sql).
    digestTime: r.digest_time || null,
    reminderMinutes: r.reminder_minutes || null,
    tz: r.tz || null,
    // Austrian public holidays in this calendar (supabase/shared_holidays.sql).
    holidaysEnabled: !!r.holidays_enabled,
    holidayCategoryId: r.holiday_category_id || 'red'
  }));
}

// Every shared-calendar birthday row visible to the current user. Each item
// carries its sharedCalendarId so the UI can filter per view.
async function loadSharedCalendarBirthdays() {
  const { data, error } = await supabase
    .from('shared_calendar_birthdays')
    .select('id,calendar_id,data');
  if (error) throw error;
  return (data || []).map((r) => ({
    ...(r.data || {}),
    id: r.id,
    sharedCalendarId: r.calendar_id || null
  }));
}

// Load every shared-calendar category row visible to the current user.
// Returns a flat array; each item carries its sharedCalendarId so Calendar.js
// can filter per view.
async function loadSharedCalendarCategories() {
  const { data, error } = await supabase
    .from('shared_calendar_categories')
    .select('id,calendar_id,data');
  if (error) throw error;
  return (data || []).map((r) => ({
    ...(r.data || {}),
    id: r.id,
    sharedCalendarId: r.calendar_id || null
  }));
}

async function loadSharedShoppingLists() {
  const { data, error } = await supabase
    .from('shared_shopping_lists')
    .select('id,name,owner_id,created_at')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    name: r.name,
    ownerId: r.owner_id,
    createdAt: r.created_at
  }));
}

async function loadSingleton(table) {
  const { data, error } = await supabase.from(table).select('*').maybeSingle();
  if (error && error.code !== 'PGRST116') throw error;
  return data || null;
}

// Load todo_tasks incl. sharing columns. Pre-migration (no
// shared_calendar_id column yet) the select falls back to the legacy shape
// and everything counts as the user's own, personal-only tasks.
async function loadTodoTasks() {
  const { data, error } = await supabase
    .from('todo_tasks')
    .select('id,data,shared_calendar_id,user_id');
  if (!error) {
    return (data || []).map((row) => ({
      ...(row.data || {}),
      id: row.id,
      sharedCalendarId: row.shared_calendar_id || null,
      createdBy: row.user_id || null
    }));
  }
  // eslint-disable-next-line no-console
  console.warn(
    '[dataSync] todo_tasks lacks the sharing columns — run supabase/todo_calendar_sharing.sql; todos stay personal-only until then'
  );
  const fallback = await supabase.from('todo_tasks').select('id,data');
  if (fallback.error) throw fallback.error;
  return (fallback.data || []).map((row) => ({ ...(row.data || {}), id: row.id }));
}

// Load fitness_workouts → group by sport_name → workoutsBySport.
async function loadWorkouts() {
  const { data, error } = await supabase.from('fitness_workouts').select('id,sport_name,data');
  if (error) throw error;
  const map = {};
  for (const row of data || []) {
    const sport = row.sport_name;
    if (!map[sport]) map[sport] = [];
    map[sport].push({ ...(row.data || {}), id: row.id });
  }
  return map;
}

// Tables whose defaults must be persisted the FIRST time we see them empty.
// Without seeding, defaults only exist in memory: the user's first edit
// upserts ONE row, and the next load would treat that single row as the
// whole collection — silently evicting every other default.
const SEEDED_TABLES = {
  budget_expense_categories: () => defaultData.budget.expenseCategories,
  budget_income_categories: () => defaultData.budget.incomeCategories,
  budget_savings_categories: () => defaultData.budget.savingsCategories
};

async function seedIfEmpty(table, userId, items) {
  try {
    await upsertCollection(table, userId, JSON.parse(JSON.stringify(items)));
    return true;
  } catch (err) {
    // Offline or RLS hiccup — keep the in-memory defaults for this session;
    // the next successful load will retry the seeding.
    // eslint-disable-next-line no-console
    console.warn(`[dataSync] seeding ${table} failed`, err);
    return false;
  }
}

export async function loadAll(userId) {
  // Run all loads in parallel.
  const collectionResults = await Promise.all(
    COLLECTIONS.map((c) =>
      loadCollection(c.table, c.columns, c.mapRow).then((items) => [c, items])
    )
  );

  // One-time seeding of default rows for empty "config" collections (see
  // SEEDED_TABLES). Idempotent (upsert on user_id,id), so a concurrent
  // initial-load + poll pair can't duplicate anything.
  await Promise.all(
    collectionResults
      .filter(([c, items]) => SEEDED_TABLES[c.table] && (!items || items.length === 0))
      .map(([c]) => seedIfEmpty(c.table, userId, SEEDED_TABLES[c.table]()))
  );

  const [
    todoTaskRows,
    userSettings,
    calSettings,
    fitSettings,
    workoutsBySport,
    sharedCalendars,
    sharedCategories,
    personalCategories,
    sharedBirthdays,
    personalBirthdays,
    calendarEvents,
    sharedShoppingLists,
    shoppingRecipes,
    shoppingItems
  ] = await Promise.all([
    loadTodoTasks(),
    loadSingleton('user_settings'),
    loadSingleton('calendar_settings'),
    loadSingleton('fitness_settings'),
    loadWorkouts(),
    loadSharedCalendars(),
    loadSharedCalendarCategories(),
    loadCollection('calendar_categories'),
    loadSharedCalendarBirthdays(),
    loadCollection('calendar_birthdays'),
    loadCollection('calendar_events', 'id,data,shared_calendar_id,user_id', (row) => ({
      ...(row.data || {}),
      id: row.id,
      sharedCalendarId: row.shared_calendar_id || null,
      createdBy: row.user_id || null
    })),
    loadSharedShoppingLists(),
    loadCollection('shopping_recipes', 'id,data,shared_list_id,user_id', (row) => ({
      ...(row.data || {}),
      id: row.id,
      sharedListId: row.shared_list_id || null,
      createdBy: row.user_id || null
    })),
    loadCollection('shopping_items', 'id,data,shared_list_id,user_id', (row) => ({
      ...(row.data || {}),
      id: row.id,
      sharedListId: row.shared_list_id || null,
      createdBy: row.user_id || null
    }))
  ]);

  // Start from defaults so any missing pieces are still present.
  let merged = JSON.parse(JSON.stringify(defaultData));
  merged.calendar.events = calendarEvents || [];
  merged.shopping = {
    ...(merged.shopping || {}),
    recipes: shoppingRecipes || [],
    items: shoppingItems || [],
    sharedLists: (sharedShoppingLists || []).map((l) => ({
      ...l,
      isOwner: l.ownerId === userId
    }))
  };

  // If the user has calendar_categories rows, replace defaults entirely so
  // we don't end up with duplicates of the seeded labels.
  const seenCollections = new Set();
  for (const [c, items] of collectionResults) {
    seenCollections.add(c.table);
    if (items && items.length > 0) {
      merged = setPath(merged, c.path, items);
    } else {
      // Empty collection — keep the defaults if this is a "config" collection
      // that ships with seed values (calendar.categories, budget.*Categories).
      // Otherwise force an empty array.
      const isSeeded =
        c.table === 'calendar_categories' ||
        c.table === 'budget_expense_categories' ||
        c.table === 'budget_income_categories' ||
        c.table === 'budget_savings_categories';
      if (!isSeeded) merged = setPath(merged, c.path, []);
    }
  }

  // Todo tasks: RLS returns the user's own rows PLUS rows other members
  // shared into a calendar the user belongs to. Own rows are the editable
  // task list; foreign rows are display-only (calendar view) and must never
  // enter the sync diff.
  merged.todo.tasks = (todoTaskRows || []).filter(
    (t) => !t.createdBy || t.createdBy === userId
  );
  merged.todo.sharedTasks = (todoTaskRows || []).filter(
    (t) => t.createdBy && t.createdBy !== userId
  );

  // Calendar birthdays split across two tables like categories. Personal
  // rows (`sharedCalendarId === null`) live in `calendar_birthdays`; shared
  // rows live in `shared_calendar_birthdays` scoped by calendar.
  const tagPersonalBdays = (arr) =>
    (arr || []).map((b) => ({ ...b, sharedCalendarId: null }));
  merged.calendar.birthdays = [
    ...tagPersonalBdays(personalBirthdays),
    ...(sharedBirthdays || [])
  ];

  // Calendar categories split across two tables:
  //   - personal (calendar_categories)   → sharedCalendarId === null
  //   - shared   (shared_calendar_categories) → sharedCalendarId set
  // Keep defaults only when the user has no personal rows yet — and persist
  // them right away (same eviction hazard as SEEDED_TABLES above).
  const tagPersonal = (arr) =>
    (arr || []).map((c) => ({ ...c, sharedCalendarId: null }));
  if (!personalCategories || personalCategories.length === 0) {
    await seedIfEmpty('calendar_categories', userId, defaultData.calendar.categories);
  }
  const personal =
    personalCategories && personalCategories.length > 0
      ? tagPersonal(personalCategories)
      : tagPersonal(merged.calendar.categories);
  merged.calendar.categories = [...personal, ...(sharedCategories || [])];

  // Singletons
  if (userSettings) {
    merged.settings = {
      ...merged.settings,
      currency: userSettings.currency ?? merged.settings.currency,
      theme: userSettings.theme ?? merged.settings.theme,
      enabledFeatures: {
        ...merged.settings.enabledFeatures,
        ...(userSettings.enabled_features || {})
      },
      groupOrder: (() => {
        // Start from the user's saved order, then append any default
        // groups that aren't yet in it (e.g. newly added features like
        // `fuel`). This way new sidebar entries appear automatically for
        // returning users without resetting their custom ordering.
        const saved = Array.isArray(userSettings.group_order) && userSettings.group_order.length
          ? [...userSettings.group_order]
          : [...merged.settings.groupOrder];
        for (const gid of merged.settings.groupOrder || []) {
          if (!saved.includes(gid)) saved.push(gid);
        }
        return saved;
      })(),
      showPersonalCalendar:
        userSettings.show_personal_calendar !== undefined
          ? !!userSettings.show_personal_calendar
          : merged.settings.showPersonalCalendar,
      notificationPrefs:
        userSettings.notification_prefs && typeof userSettings.notification_prefs === 'object'
          ? userSettings.notification_prefs
          : (merged.settings.notificationPrefs || {}),
      dashboardLayout:
        userSettings.dashboard_layout && typeof userSettings.dashboard_layout === 'object'
          ? userSettings.dashboard_layout
          : null,
      navTabs: Array.isArray(userSettings.nav_tabs) ? userSettings.nav_tabs : []
    };
  }

  if (calSettings) {
    merged.calendar.birthdaySettings = {
      linkedToCalendar: !!calSettings.linked_to_calendar,
      categoryId: calSettings.category_id || merged.calendar.birthdaySettings.categoryId
    };
    // Austrian public holidays ride the same row; the columns come from
    // supabase/holidays.sql. Only take the server's word when the column is
    // actually there — on an un-migrated DB the row simply lacks it, and
    // reading `undefined` as "off" would clobber the local setting on every
    // reload.
    if ('holidays_enabled' in calSettings) {
      merged.calendar.holidaySettings = {
        enabled: !!calSettings.holidays_enabled,
        categoryId:
          calSettings.holiday_category_id ||
          (merged.calendar.holidaySettings && merged.calendar.holidaySettings.categoryId) ||
          'red'
      };
    }
    // Todo→calendar link settings ride the same singleton row. The columns
    // may not exist yet on an un-migrated DB (select('*') tolerates that);
    // then these stay at their defaults.
    merged.todo.calendarSettings = {
      linkedToCalendar: !!calSettings.todo_linked,
      categoryId: calSettings.todo_category_id || 'blue',
      // Which calendar the todos target (null = personal calendar).
      sharedCalendarId: calSettings.todo_shared_calendar_id || null
    };
  }

  merged.fitness.workoutsBySport = workoutsBySport || {};
  merged.fitness.calendarLinksBySport =
    (fitSettings && fitSettings.calendar_links_by_sport) || {};
  // Crossfit PRs + workout structure ride the fitness_settings singleton
  // (columns added by supabase/settings_migration.sql; absent pre-migration).
  // crossfitRecords is an object map keyed by exercise name.
  merged.fitness.crossfitRecords =
    (fitSettings && fitSettings.crossfit_records) || {};
  merged.fitness.workoutStructure =
    (fitSettings && fitSettings.workout_structure) || null;

  // Shared calendars the current user can access (owner or member).
  merged.calendar.sharedCalendars = (sharedCalendars || []).map((c) => ({
    ...c,
    isOwner: c.ownerId === userId
  }));

  return merged;
}

// ---- Syncing ---------------------------------------------------------------
async function upsertCollection(table, userId, items, extraFn) {
  if (!items.length) return;
  const rows = items.map((raw) => {
    const it = ensureId(raw);
    const extra = extraFn ? extraFn(it) : {};
    return {
      id: String(it.id),
      user_id: userId,
      data: it,
      ...extra
    };
  });
  const { error } = await supabase.from(table).upsert(rows, { onConflict: 'user_id,id' });
  if (error) throw error;
}

async function deleteFromCollection(table, userId, ids) {
  if (!ids.length) return;
  const { error } = await supabase
    .from(table)
    .delete()
    .eq('user_id', userId)
    .in('id', ids.map(String));
  if (error) throw error;
}

async function syncCollection(c, userId, prev, next) {
  const oldArr = getPath(prev, c.path) || [];
  const newArr = getPath(next, c.path) || [];
  if (sameRef(oldArr, newArr)) return;
  const { upserts, deletes } = diffArray(oldArr, newArr);
  if (upserts.length) await upsertCollection(c.table, userId, upserts, c.extra);
  if (deletes.length) await deleteFromCollection(c.table, userId, deletes);
}

async function syncWorkouts(userId, prev, next) {
  const oldMap = (prev.fitness && prev.fitness.workoutsBySport) || {};
  const newMap = (next.fitness && next.fitness.workoutsBySport) || {};
  if (sameRef(oldMap, newMap)) return;

  // Flatten with sport_name attached
  const flatten = (map) => {
    const out = [];
    for (const [sport, list] of Object.entries(map || {})) {
      for (const w of list || []) out.push({ ...w, _sport: sport });
    }
    return out;
  };
  const oldFlat = flatten(oldMap);
  const newFlat = flatten(newMap);
  const { upserts, deletes } = diffArray(oldFlat, newFlat);

  if (upserts.length) {
    const rows = upserts.map((raw) => {
      const it = ensureId(raw);
      const { _sport, ...rest } = it;
      return {
        id: String(it.id),
        user_id: userId,
        sport_name: _sport,
        date: rest.date || null,
        data: rest
      };
    });
    const { error } = await supabase
      .from('fitness_workouts')
      .upsert(rows, { onConflict: 'user_id,id' });
    if (error) throw error;
  }
  if (deletes.length) {
    await deleteFromCollection('fitness_workouts', userId, deletes);
  }
}

// Sync the user's OWN todo tasks (data.todo.tasks — foreign shared tasks
// live in data.todo.sharedTasks and are never written by this client).
// Falls back to the legacy row shape while todo_calendar_sharing.sql hasn't
// been applied yet.
async function syncTodoTasks(userId, prev, next) {
  const oldArr = (prev.todo && prev.todo.tasks) || [];
  const newArr = (next.todo && next.todo.tasks) || [];
  if (sameRef(oldArr, newArr)) return;
  const { upserts, deletes } = diffArray(oldArr, newArr);
  if (upserts.length) {
    const rows = upserts.map((raw) => {
      const it = ensureId(raw);
      // Keep derived/foreign-only fields out of the data JSON.
      // eslint-disable-next-line no-unused-vars
      const { sharedCalendarId: _s, createdBy: _c, ...rest } = it;
      return {
        id: String(it.id),
        user_id: userId,
        data: rest,
        done: !!it.done,
        date: it.date || null,
        shared_calendar_id: it.sharedCalendarId || null
      };
    });
    let { error } = await supabase
      .from('todo_tasks')
      .upsert(rows, { onConflict: 'user_id,id' });
    if (error && isMissingColumnError(error)) {
      // eslint-disable-next-line no-console
      console.warn(
        '[dataSync] todo_tasks lacks the sharing columns — run supabase/todo_calendar_sharing.sql'
      );
      ({ error } = await supabase
        .from('todo_tasks')
        .upsert(
          rows.map(({ shared_calendar_id: _sc, ...r }) => r),
          { onConflict: 'user_id,id' }
        ));
    }
    if (error) throw error;
  }
  if (deletes.length) {
    await deleteFromCollection('todo_tasks', userId, deletes);
  }
}

async function syncUserSettings(userId, prev, next) {
  const oldS = prev.settings || {};
  const newS = next.settings || {};
  if (
    oldS.currency === newS.currency &&
    oldS.theme === newS.theme &&
    equalJSON(oldS.enabledFeatures, newS.enabledFeatures) &&
    equalJSON(oldS.groupOrder, newS.groupOrder) &&
    (oldS.showPersonalCalendar !== false) === (newS.showPersonalCalendar !== false) &&
    equalJSON(oldS.notificationPrefs || {}, newS.notificationPrefs || {}) &&
    equalJSON(oldS.dashboardLayout || null, newS.dashboardLayout || null) &&
    equalJSON(oldS.navTabs || [], newS.navTabs || [])
  ) return;
  const base = {
    user_id: userId,
    currency: newS.currency || 'USD',
    theme: newS.theme || 'light',
    enabled_features: newS.enabledFeatures || {},
    group_order: newS.groupOrder || [],
    show_personal_calendar: newS.showPersonalCalendar !== false,
    notification_prefs: newS.notificationPrefs || {}
  };
  let { error } = await supabase.from('user_settings').upsert(
    {
      ...base,
      dashboard_layout: newS.dashboardLayout || null,
      nav_tabs: newS.navTabs || []
    },
    { onConflict: 'user_id' }
  );
  if (error && isMissingColumnError(error)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[dataSync] user_settings lacks dashboard_layout/nav_tabs — run supabase/dashboard_layout.sql; dashboard layout and tab bar will not persist until then'
    );
    ({ error } = await supabase
      .from('user_settings')
      .upsert(base, { onConflict: 'user_id' }));
  }
  if (error) throw error;
}

// PostgREST reports a payload column that doesn't exist in the table as
// PGRST204. Used to degrade gracefully before supabase/settings_migration.sql
// has been applied: retry without the new columns instead of failing the sync.
const isMissingColumnError = (err) =>
  !!err && (err.code === 'PGRST204' || /column/i.test(err.message || ''));

async function syncCalendarSettings(userId, prev, next) {
  const oldB = (prev.calendar && prev.calendar.birthdaySettings) || {};
  const newB = (next.calendar && next.calendar.birthdaySettings) || {};
  const oldT = (prev.todo && prev.todo.calendarSettings) || {};
  const newT = (next.todo && next.todo.calendarSettings) || {};
  const oldH = (prev.calendar && prev.calendar.holidaySettings) || {};
  const newH = (next.calendar && next.calendar.holidaySettings) || {};
  if (equalJSON(oldB, newB) && equalJSON(oldT, newT) && equalJSON(oldH, newH)) return;
  const legacy = {
    user_id: userId,
    linked_to_calendar: !!newB.linkedToCalendar,
    category_id: newB.categoryId || null
  };
  const v1 = {
    ...legacy,
    todo_linked: !!newT.linkedToCalendar,
    todo_category_id: newT.categoryId || null
  };
  const v2 = { ...v1, todo_shared_calendar_id: newT.sharedCalendarId || null };
  // Tiered fallback across the migrations (settings_migration.sql adds the
  // todo columns, todo_calendar_sharing.sql the target calendar,
  // holidays.sql the public-holiday columns).
  let { error } = await supabase.from('calendar_settings').upsert(
    { ...v2, holidays_enabled: !!newH.enabled, holiday_category_id: newH.categoryId || null },
    { onConflict: 'user_id' }
  );
  if (error && isMissingColumnError(error)) {
    ({ error } = await supabase.from('calendar_settings').upsert(v2, { onConflict: 'user_id' }));
    if (!error) {
      // eslint-disable-next-line no-console
      console.warn(
        '[dataSync] calendar_settings lacks the holiday columns — run supabase/holidays.sql; the Feiertage setting will not persist until then'
      );
    }
  }
  if (error && isMissingColumnError(error)) {
    ({ error } = await supabase
      .from('calendar_settings')
      .upsert(v1, { onConflict: 'user_id' }));
    if (!error) {
      // eslint-disable-next-line no-console
      console.warn(
        '[dataSync] calendar_settings lacks todo_shared_calendar_id — run supabase/todo_calendar_sharing.sql'
      );
    }
  }
  if (error && isMissingColumnError(error)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[dataSync] calendar_settings lacks the todo columns — run supabase/settings_migration.sql; todo calendar-link settings will not persist until then'
    );
    ({ error } = await supabase
      .from('calendar_settings')
      .upsert(legacy, { onConflict: 'user_id' }));
  }
  if (error) throw error;
}

async function syncFitnessSettings(userId, prev, next) {
  const oldLinks = (prev.fitness && prev.fitness.calendarLinksBySport) || {};
  const newLinks = (next.fitness && next.fitness.calendarLinksBySport) || {};
  const oldRecords = (prev.fitness && prev.fitness.crossfitRecords) || {};
  const newRecords = (next.fitness && next.fitness.crossfitRecords) || {};
  const oldStructure = (prev.fitness && prev.fitness.workoutStructure) || null;
  const newStructure = (next.fitness && next.fitness.workoutStructure) || null;
  if (
    equalJSON(oldLinks, newLinks) &&
    equalJSON(oldRecords, newRecords) &&
    equalJSON(oldStructure, newStructure)
  )
    return;
  const legacy = {
    user_id: userId,
    calendar_links_by_sport: newLinks
  };
  let { error } = await supabase.from('fitness_settings').upsert(
    {
      ...legacy,
      crossfit_records: newRecords,
      workout_structure: newStructure
    },
    { onConflict: 'user_id' }
  );
  if (error && isMissingColumnError(error)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[dataSync] fitness_settings lacks the records columns — run supabase/settings_migration.sql; Crossfit PRs / workout structure will not persist until then'
    );
    ({ error } = await supabase
      .from('fitness_settings')
      .upsert(legacy, { onConflict: 'user_id' }));
  }
  if (error) throw error;
}

// Calendar categories live in two tables. Partition by scope and sync each
// half with its respective table. An item's scope is immutable (categories
// don't migrate between personal and shared), so treating moves as a delete
// in one table + insert in the other isn't needed in practice.
async function syncCalendarCategories(userId, prev, next) {
  const oldAll = (prev.calendar && prev.calendar.categories) || [];
  const newAll = (next.calendar && next.calendar.categories) || [];
  if (sameRef(oldAll, newAll)) return;

  const partition = (arr) => {
    const personal = [];
    const shared = [];
    for (const it of arr || []) {
      if (it.sharedCalendarId) shared.push(it);
      else personal.push(it);
    }
    return { personal, shared };
  };

  const oldP = partition(oldAll);
  const newP = partition(newAll);

  // -- personal half: reuse the standard collection helpers
  const personalDiff = diffArray(oldP.personal, newP.personal);
  const personalUpserts = personalDiff.upserts.map((raw) => {
    const it = ensureId(raw);
    // Don't persist sharedCalendarId in the data JSON for personal rows.
    // eslint-disable-next-line no-unused-vars
    const { sharedCalendarId: _s, ...rest } = it;
    return { id: String(it.id), user_id: userId, data: rest };
  });
  if (personalUpserts.length) {
    const { error } = await supabase
      .from('calendar_categories')
      .upsert(personalUpserts, { onConflict: 'user_id,id' });
    if (error) throw error;
  }
  if (personalDiff.deletes.length) {
    await deleteFromCollection('calendar_categories', userId, personalDiff.deletes);
  }

  // -- shared half: group by calendar_id for upsert & delete
  // Include sharedCalendarId in the diff key so a rename in one calendar and
  // an identical-id row in another calendar don't mask each other.
  const sharedKey = (it) => `${it.sharedCalendarId}::${it.id}`;
  const oldShared = new Map((oldP.shared || []).map((it) => [sharedKey(it), it]));
  const newShared = new Map((newP.shared || []).map((it) => [sharedKey(it), it]));
  const sharedUpserts = [];
  const sharedDeletes = [];
  for (const [k, it] of newShared) {
    const prevIt = oldShared.get(k);
    if (!prevIt || !equalJSON(prevIt, it)) sharedUpserts.push(it);
  }
  for (const [k, it] of oldShared) {
    if (!newShared.has(k)) sharedDeletes.push(it);
  }

  if (sharedUpserts.length) {
    const rows = sharedUpserts.map((raw) => {
      const it = ensureId(raw);
      const { sharedCalendarId, ...rest } = it;
      return {
        id: String(it.id),
        calendar_id: sharedCalendarId,
        data: rest
      };
    });
    const { error } = await supabase
      .from('shared_calendar_categories')
      .upsert(rows, { onConflict: 'calendar_id,id' });
    if (error) throw error;
  }
  if (sharedDeletes.length) {
    // Delete row-by-row: composite (calendar_id, id) keys.
    await Promise.all(
      sharedDeletes.map((it) =>
        supabase
          .from('shared_calendar_categories')
          .delete()
          .eq('calendar_id', it.sharedCalendarId)
          .eq('id', String(it.id))
          .then(({ error }) => {
            if (error) throw error;
          })
      )
    );
  }
}

// Like syncCalendarCategories, but for birthdays. Personal rows go to
// `calendar_birthdays`, shared rows to `shared_calendar_birthdays`.
async function syncCalendarBirthdays(userId, prev, next) {
  const oldAll = (prev.calendar && prev.calendar.birthdays) || [];
  const newAll = (next.calendar && next.calendar.birthdays) || [];
  if (sameRef(oldAll, newAll)) return;

  const partition = (arr) => {
    const personal = [];
    const shared = [];
    for (const it of arr || []) {
      if (it.sharedCalendarId) shared.push(it);
      else personal.push(it);
    }
    return { personal, shared };
  };

  const oldP = partition(oldAll);
  const newP = partition(newAll);

  // Personal half.
  const pDiff = diffArray(oldP.personal, newP.personal);
  if (pDiff.upserts.length) {
    const rows = pDiff.upserts.map((raw) => {
      const it = ensureId(raw);
      // eslint-disable-next-line no-unused-vars
      const { sharedCalendarId: _s, ...rest } = it;
      return { id: String(it.id), user_id: userId, data: rest };
    });
    const { error } = await supabase
      .from('calendar_birthdays')
      .upsert(rows, { onConflict: 'user_id,id' });
    if (error) throw error;
  }
  if (pDiff.deletes.length) {
    await deleteFromCollection('calendar_birthdays', userId, pDiff.deletes);
  }

  // Shared half — diff keyed by (sharedCalendarId, id) so per-calendar
  // changes don't collide.
  const sharedKey = (it) => `${it.sharedCalendarId}::${it.id}`;
  const oldShared = new Map((oldP.shared || []).map((it) => [sharedKey(it), it]));
  const newShared = new Map((newP.shared || []).map((it) => [sharedKey(it), it]));
  const sUpserts = [];
  const sDeletes = [];
  for (const [k, it] of newShared) {
    const prevIt = oldShared.get(k);
    if (!prevIt || !equalJSON(prevIt, it)) sUpserts.push(it);
  }
  for (const [k, it] of oldShared) {
    if (!newShared.has(k)) sDeletes.push(it);
  }
  if (sUpserts.length) {
    const rows = sUpserts.map((raw) => {
      const it = ensureId(raw);
      const { sharedCalendarId, ...rest } = it;
      return {
        id: String(it.id),
        calendar_id: sharedCalendarId,
        data: rest
      };
    });
    const { error } = await supabase
      .from('shared_calendar_birthdays')
      .upsert(rows, { onConflict: 'calendar_id,id' });
    if (error) throw error;
  }
  if (sDeletes.length) {
    await Promise.all(
      sDeletes.map((it) =>
        supabase
          .from('shared_calendar_birthdays')
          .delete()
          .eq('calendar_id', it.sharedCalendarId)
          .eq('id', String(it.id))
          .then(({ error }) => {
            if (error) throw error;
          })
      )
    );
  }
}

// Custom syncer for calendar_events. Unlike the generic helper, this one
// distinguishes UPDATE (existing row, possibly owned by another shared-
// calendar member) from INSERT (new row, always under the current user).
// We can't use a single upsert here because the INSERT WITH CHECK policy
// requires `user_id = auth.uid()`, so we'd be unable to write rows under
// the original creator's id even though the UPDATE policy permits it.
async function syncCalendarEvents(userId, prev, next) {
  const oldArr = (prev.calendar && prev.calendar.events) || [];
  const newArr = (next.calendar && next.calendar.events) || [];
  if (sameRef(oldArr, newArr)) return;

  const oldById = new Map(oldArr.map((it) => [String(it.id), it]));
  const newById = new Map(newArr.map((it) => [String(it.id), it]));

  const inserts = [];
  const updates = []; // [{ ownerId, id, patch }]
  for (const [id, it] of newById) {
    const prevIt = oldById.get(id);
    if (prevIt && equalJSON(prevIt, it)) continue;
    // Strip transient fields from the JSON payload.
    // eslint-disable-next-line no-unused-vars
    const { createdBy: _c, sharedCalendarId: _s, ...dataJson } = it;
    const patch = {
      data: dataJson,
      date: it.date || null,
      shared_calendar_id: it.sharedCalendarId || null
    };
    if (prevIt) {
      // Existing row → UPDATE under whoever currently owns it. RLS allows
      // any shared-calendar member to update; for personal events the
      // owner is always the current user.
      updates.push({
        ownerId: prevIt.createdBy || userId,
        id: String(id),
        patch
      });
    } else {
      // Brand-new row → INSERT under the current user. WITH CHECK is
      // satisfied because user_id = auth.uid().
      inserts.push({
        id: String(id),
        user_id: userId,
        ...patch
      });
    }
  }

  // Updates: one query per row (different owners can't be batched in a
  // single UPDATE). These are rare (only when an event actually changes).
  await Promise.all(
    updates.map(({ ownerId, id, patch }) =>
      supabase
        .from('calendar_events')
        .update(patch)
        .eq('user_id', ownerId)
        .eq('id', id)
        .then(({ error }) => {
          if (error) throw error;
        })
    )
  );
  if (inserts.length) {
    const { error } = await supabase.from('calendar_events').insert(inserts);
    if (error) throw error;
  }

  // Deletes — group by owner so we issue one DELETE per user_id.
  const byOwner = new Map();
  for (const [id, it] of oldById) {
    if (newById.has(id)) continue;
    const ownerId = it.createdBy || userId;
    if (!byOwner.has(ownerId)) byOwner.set(ownerId, []);
    byOwner.get(ownerId).push(String(id));
  }
  for (const [ownerId, ids] of byOwner) {
    const { error } = await supabase
      .from('calendar_events')
      .delete()
      .eq('user_id', ownerId)
      .in('id', ids);
    if (error) throw error;
  }
}

// Custom syncer for shopping_recipes / shopping_items. Same logic as
// syncCalendarEvents: distinguish UPDATE (existing row, owned by any list
// member) from INSERT (always under current user).
async function syncShoppingTable(table, userId, prev, next, path) {
  const oldArr = getPath(prev, path) || [];
  const newArr = getPath(next, path) || [];
  if (sameRef(oldArr, newArr)) return;

  const oldById = new Map(oldArr.map((it) => [String(it.id), it]));
  const newById = new Map(newArr.map((it) => [String(it.id), it]));

  const inserts = [];
  const updates = [];
  for (const [id, it] of newById) {
    const prevIt = oldById.get(id);
    if (prevIt && equalJSON(prevIt, it)) continue;
    // eslint-disable-next-line no-unused-vars
    const { createdBy: _c, sharedListId: _s, ...dataJson } = it;
    const patch = {
      data: dataJson,
      shared_list_id: it.sharedListId || null
    };
    if (prevIt) {
      updates.push({
        ownerId: prevIt.createdBy || userId,
        id: String(id),
        patch
      });
    } else {
      inserts.push({
        id: String(id),
        user_id: userId,
        ...patch
      });
    }
  }

  await Promise.all(
    updates.map(({ ownerId, id, patch }) =>
      supabase
        .from(table)
        .update(patch)
        .eq('user_id', ownerId)
        .eq('id', id)
        .then(({ error }) => {
          if (error) throw error;
        })
    )
  );
  if (inserts.length) {
    const { error } = await supabase.from(table).insert(inserts);
    if (error) throw error;
  }

  // Deletes — group by owner.
  const byOwner = new Map();
  for (const [id, it] of oldById) {
    if (newById.has(id)) continue;
    const ownerId = it.createdBy || userId;
    if (!byOwner.has(ownerId)) byOwner.set(ownerId, []);
    byOwner.get(ownerId).push(String(id));
  }
  for (const [ownerId, ids] of byOwner) {
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('user_id', ownerId)
      .in('id', ids);
    if (error) throw error;
  }
}

const syncShoppingRecipes = (uid, p, n) =>
  syncShoppingTable('shopping_recipes', uid, p, n, ['shopping', 'recipes']);
const syncShoppingItems = (uid, p, n) =>
  syncShoppingTable('shopping_items', uid, p, n, ['shopping', 'items']);

export async function syncData(userId, prev, next) {
  const tasks = [];
  for (const c of COLLECTIONS) tasks.push(syncCollection(c, userId, prev, next));
  tasks.push(syncTodoTasks(userId, prev, next));
  tasks.push(syncCalendarEvents(userId, prev, next));
  tasks.push(syncCalendarCategories(userId, prev, next));
  tasks.push(syncCalendarBirthdays(userId, prev, next));
  tasks.push(syncWorkouts(userId, prev, next));
  tasks.push(syncUserSettings(userId, prev, next));
  tasks.push(syncCalendarSettings(userId, prev, next));
  tasks.push(syncFitnessSettings(userId, prev, next));
  tasks.push(syncShoppingRecipes(userId, prev, next));
  tasks.push(syncShoppingItems(userId, prev, next));
  await Promise.all(tasks);
}

// ---- Bulk import (used by Settings "Import from JSON") ---------------------
// Replaces the user's data entirely with `data`.
export async function importAll(userId, data) {
  // Easy path: pretend prev is empty defaults so syncData inserts everything.
  const empty = JSON.parse(JSON.stringify(defaultData));
  // Also clear empty arrays that defaultData seeds with categories — we want
  // the imported data's categories to win. Make `prev` look like everything's
  // empty arrays so diff produces inserts only (no deletes since DB is empty
  // for a fresh user).
  for (const c of COLLECTIONS) {
    let cursor = empty;
    for (let i = 0; i < c.path.length - 1; i++) cursor = cursor[c.path[i]];
    cursor[c.path[c.path.length - 1]] = [];
  }
  empty.fitness.workoutsBySport = {};
  empty.fitness.calendarLinksBySport = {};
  empty.fitness.crossfitRecords = {};
  empty.fitness.workoutStructure = null;
  empty.calendar.birthdaySettings = {};
  empty.calendar.holidaySettings = {};
  empty.todo.calendarSettings = {};
  // todo_tasks left COLLECTIONS (custom syncer), so empty it explicitly.
  empty.todo.tasks = [];

  await syncData(userId, empty, data);
}
